import React, { ButtonHTMLAttributes, MouseEventHandler, ReactNode } from 'react';
import styled from 'styled-components/macro';
import { NavLink } from 'react-router-dom';
import cx from 'classnames';

type Props = {
  ariaLabel?: string;
  children?: ReactNode;
  className?: string;
  disabled?: boolean;
  loading?: boolean;
  onClick?: MouseEventHandler;
  spinnerClass?: string;
  target?: string;
  type?: ButtonHTMLAttributes<HTMLButtonElement>['type'];
  url?: string;
};

export const Button = (props: Props) => {
  const {
    className,
    spinnerClass,
    children,
    onClick,
    url,
    ariaLabel,
    target,
    loading,
    disabled,
    type = 'button',
  } = props;
  const spinnerMarkup = loading ? <Spinner className={spinnerClass} /> : null;
  const rel = 'nofollow noopener noreferrer';

  const content = (
    <>
      {spinnerMarkup}
      {children}
    </>
  );

  if (url) {
    if (/^https?:/.test(url)) {
      return (
        <a href={url} target={target || '_blank'} rel={rel} className={className}>
          {content}
        </a>
      );
    }

    return (
      <NavLink to={url} {...(props.target ? { target: props.target, rel } : {})} className={cx('man-ui', className)}>
        {content}
      </NavLink>
    );
  }

  return (
    <button
      aria-label={ariaLabel}
      type={type}
      className={cx('man-ui', { loading }, className)}
      onClick={onClick}
      disabled={disabled}
    >
      {content}
    </button>
  );
};

export const StyledButton = styled(Button)`
  position: relative;
  border-radius: 4px;

  &.loading {
    color: transparent;
  }

  @-webkit-keyframes spin {
    from {
      -webkit-transform: rotate(0deg);
    }
    to {
      -webkit-transform: rotate(360deg);
    }
  }

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`;

const SpinnerWrap = styled.span`
  position: absolute;
  width: 20px;
  height: 20px;
  display: flex;
  justify-content: center;
  align-items: center;

  .spinner {
    animation: spin 0.5s linear infinite;
    -webkit-animation: spin 0.5s linear infinite;

    path {
      fill: #888888;
    }
  }
`;

type SpinnerProps = {
  className?: string;
};

const Spinner = ({ className = 'spinner-wrap' }: SpinnerProps) => (
  <SpinnerWrap className={className}>
    <svg viewBox="0 0 20 20" className="spinner">
      <path d="M7.229 1.173a9.25 9.25 0 1 0 11.655 11.412 1.25 1.25 0 1 0-2.4-.698 6.75 6.75 0 1 1-8.506-8.329 1.25 1.25 0 1 0-.75-2.385z" />
    </svg>
  </SpinnerWrap>
);
